"use client";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

/**
 * Error boundary for the signed-out pages, sign-in among them.
 *
 * This segment had none, so anything thrown here escalated to `global-error.tsx` and the
 * member got the unstyled Next.js error page with no way back — which is how
 * THE-SUKHA-COMPANY-RX presented: a browser stream error during the navigation that
 * follows a successful sign-in, turned into a full-page crash for someone who had just
 * got their credentials right.
 *
 * `reset()` re-renders the segment, which is the right answer for a transient failure
 * mid-navigation and costs nothing when the failure is not transient — the member ends up
 * back here.
 */
export default function PublicError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("Common");

  useEffect(() => {
    // Reported here rather than left to bubble: this boundary stops the error reaching
    // `global-error.tsx`, and a boundary that swallows what it catches is worse than no
    // boundary at all.
    Sentry.captureException(error, {
      contexts: {
        sukha: {
          boundary: "public-segment",
          digest: error?.digest ?? null,
          route:
            typeof window === "undefined" ? null : window.location.pathname,
        },
      },
    });
  }, [error]);

  return (
    <div className="flex flex-col justify-center items-center p-4 min-h-screen">
      <h2 className="mb-4 font-semibold text-primary-foreground text-xl">
        {t("publicError.title")}
      </h2>
      <p className="mb-4 text-primary-foreground">{t("publicError.message")}</p>
      <button
        onClick={reset}
        className="bg-primary hover:bg-primary/90 px-4 py-2 rounded-md text-white"
      >
        {t("publicError.button")}
      </button>
    </div>
  );
}
